// Shared presentation helpers for the office Subcontractors pages (list + detail): the
// status / agreement / certificate-status tags and their labels.
//
// SESSION AUV, design system Session 8 (People): the three local style maps (STATUS_CLASS, AGREEMENT_CLASS,
// CERT_STATUS_CLASS) and the chip are gone. Every tag is the shared StatusPill, its TONE read from the status
// registry (domains subcontractor, subcontractorAgreement and certificate, src/lib/statusRegistry.ts) so there is one
// place a tone is decided. THE WORDS ARE THIS PAGE'S OWN and are kept ("Awaiting registration", "Agreement sent",
// "Expires soon", "No expiry recorded"): the registry's labels for the same statuses read differently ("Invite
// pending", "Sent", "Expiring", "No expiry"), and changing copy is outside a presentation-only session. Recorded in
// the Session AUV handoff so the Director can choose to align them. The colour moves are: Active, Signed and Valid
// were green WORDS on a dark wash and are now the green tint pill; Pending review is amber; Expired is red.
import { useState, type ReactNode } from "react";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import type { SubcontractorStatus, SubcontractorAgreementStatus, SubCertType, SubCertStatus } from "../../lib/api";

const STATUS_LABEL: Record<SubcontractorStatus, string> = {
  pending_invite: "Awaiting registration",
  pending_review: "Pending review",
  active: "Active",
  inactive: "Inactive",
};

const AGREEMENT_LABEL: Record<SubcontractorAgreementStatus, string> = {
  none: "",
  sent: "Agreement sent",
  signed: "Signed",
  expired: "Expired",
};

// Session AUR: the type/status chip is gone from here. Both pages show the office-owned status through the shared
// StatusPill (domain "subcontractorEngagement" in src/lib/statusRegistry.ts).

export function StatusTag({ status }: { status: SubcontractorStatus }) {
  return <StatusPill word={STATUS_LABEL[status] ?? status} tone={statusEntry("subcontractor", status).tone} />;
}

export function AgreementTag({ status }: { status: SubcontractorAgreementStatus }) {
  if (status === "none") return null;
  return <StatusPill word={AGREEMENT_LABEL[status]} tone={statusEntry("subcontractorAgreement", status).tone} />;
}

export function statusLabel(status: SubcontractorStatus): string {
  return STATUS_LABEL[status] ?? status;
}

// ---- Certificates and insurance (Session AEL, D37) ----------------------------------------
// Human-readable British-English labels for every structured certificate type, mirroring the
// server's SUB_CERT_TYPE_LABELS (server/src/modules/subcontractors/subcontractorCerts.ts). The
// UI reads ONLY these labels (or the server-derived typeLabel on a certificate row), never a
// raw enum key.
export const CERT_TYPE_LABELS: Record<SubCertType, string> = {
  PUBLIC_LIABILITY: "Public liability insurance",
  EMPLOYERS_LIABILITY: "Employers liability insurance",
  PROFESSIONAL_INDEMNITY: "Professional indemnity insurance",
  CSCS: "CSCS card",
  CPCS: "CPCS card",
  NIC_EIC: "NICEIC registration",
  GAS_SAFE: "Gas Safe registration",
  ASBESTOS_AWARENESS: "Asbestos awareness",
  WORKING_AT_HEIGHT: "Working at height",
  MANUAL_HANDLING: "Manual handling",
  DBS_CHECK: "DBS check", // Session ALV; same wording as the office personnel register's own DBS row
  NVQ_LEVEL_3: "NVQ Level 3 (lift engineering)", // Session AUZ
  OTHER: "Other",
};

// The certificate-type select's option order.
export const CERT_TYPE_OPTIONS: SubCertType[] = [
  "PUBLIC_LIABILITY",
  "EMPLOYERS_LIABILITY",
  "PROFESSIONAL_INDEMNITY",
  "CSCS",
  "CPCS",
  "NIC_EIC",
  "GAS_SAFE",
  "ASBESTOS_AWARENESS",
  "WORKING_AT_HEIGHT",
  "MANUAL_HANDLING",
  // Session ALV. Ordered to match the enum in prisma/schema.prisma (before OTHER, which stays
  // last). This array is a hand-written list with no compiler check that it is COMPLETE -- unlike
  // CERT_TYPE_LABELS above, which is a Record<SubCertType, string> and fails to build if a value
  // is missed -- so a new enum value that is added to the labels but not here is silently absent
  // from the dropdown. Pinned by a test asserting the two lists hold exactly the same values.
  "DBS_CHECK",
  "NVQ_LEVEL_3",
  "OTHER",
];

const CERT_STATUS_LABEL: Record<SubCertStatus, string> = {
  expired: "Expired",
  expiring: "Expires soon",
  valid: "Valid",
  "no-expiry": "No expiry recorded",
};

// A certificate's status is derived server-side against the current time and is never stored,
// so it can never go stale. "no-expiry" is a distinct, neutral state from "valid": nothing was
// recorded to expire.
export function CertStatusTag({ status }: { status: SubCertStatus }) {
  return <StatusPill word={CERT_STATUS_LABEL[status]} tone={statusEntry("certificate", status).tone} />;
}

// The width-capped, wrap-safe portal box these dialogs introduced is now the SHARED office
// ModalShell (src/components/ModalShell.tsx, Session ACW loop 4). This local wrapper keeps the
// dialogs' original presentation exactly: top-anchored at my-16, 480px cap, p-6, no Escape or
// backdrop close (the two-step delete's cancel paths stay the explicit buttons only).
function SubcontractorModalShell({ ariaLabel, border, children }: { ariaLabel: string; border: string; children: ReactNode }) {
  return (
    <ModalShell ariaLabel={ariaLabel} border={border} margin="my-16" width="max-w-[480px]" panelClassName="p-6">
      {children}
    </ModalShell>
  );
}

// Montserrat 14px body; force normal wrapping (belt-and-braces with the portal) so long copy never
// runs past the right edge of the modal box.
function ModalBody({ children }: { children: ReactNode }) {
  return (
    <p className="text-sm font-body text-ele-body" style={{ whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word" }}>
      {children}
    </p>
  );
}

// Archive: the normal offboarding. Reversible via Restore, so a single confirmation step; the
// confirm is the brand purple primary action (not a destructive red).
export function SubcontractorArchiveDialog({ busy, onConfirm, onCancel }: { busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <SubcontractorModalShell ariaLabel="Archive subcontractor" border="border-ele-purple">
      <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-strong">Archive subcontractor?</h2>
      <ModalBody>
        This subcontractor will be removed from the active list. Their record, agreement, and documents are kept and can be
        found in the Archived tab. Use this when you stop working with a subcontractor.
      </ModalBody>
      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="accent" className={MODAL_ACTION_BTN} onClick={onConfirm} disabled={busy}>
          {busy ? "Archiving..." : "Archive"}
        </Button>
      </div>
    </SubcontractorModalShell>
  );
}

// Delete: permanent, for data-entry mistakes only. Two-step confirmation (Step 1 then Step 2) so
// the final, irreversible click is unmistakable. Only ever shown when the server-side guard would
// allow the delete (no signed agreement, no linked projects); the server enforces it regardless.
export function SubcontractorDeleteDialog({ busy, onConfirm, onCancel }: { busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  const [step, setStep] = useState<1 | 2>(1);
  function cancel() {
    setStep(1);
    onCancel();
  }
  return (
    <SubcontractorModalShell ariaLabel="Delete subcontractor" border="border-ele-danger">
      {step === 1 ? (
        <>
          <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-strong">Delete subcontractor?</h2>
          <ModalBody>
            This permanently deletes the subcontractor record and cannot be undone. Only use this for records created by
            mistake. If you have worked with this subcontractor at any point, use Archive instead.
          </ModalBody>
          <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
            <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={cancel} disabled={busy}>Cancel</Button>
            <Button variant="danger" className={MODAL_ACTION_BTN} onClick={() => setStep(2)} disabled={busy}>Yes, delete</Button>
          </div>
        </>
      ) : (
        <>
          <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-strong">Are you sure?</h2>
          <ModalBody>This cannot be undone.</ModalBody>
          <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
            <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={cancel} disabled={busy}>Cancel</Button>
            <Button variant="danger" className={MODAL_ACTION_BTN} onClick={onConfirm} disabled={busy}>{busy ? "Deleting..." : "Delete permanently"}</Button>
          </div>
        </>
      )}
    </SubcontractorModalShell>
  );
}
